import React, { useCallback, useMemo, useState } from "react";
import { ResizeSensor } from "@blueprintjs/core";
import classNames from "classnames";
import PlotlyPlot from "react-plotly.js";
import TextTruncate from "components/TextTruncate";
import BiasedIcon from "../BiasedIcon";
import { get1DPlotLayout, getAxisLimits, getPlotLayout, SpaceviewerMode, } from "rs-components/Spaceviewer/utils";
import { SpaceviewerAxisType } from "shared/types";
import { defaultPlotConfig } from "shared/plotly/defaultConfigs";
import { renderNumberValue } from "utils/string";
import * as styles from "./styles.module.scss";
const SpaceviewerGraph = ({ points, xAxis, yAxis, isEmpty, mode, widgetWidth, someResultBiased, handleGraphClick, }) => {
    const [yAxisRangeWidth, setYAxisRangeWidth] = useState(0);
    const plotLayout = useMemo(() => mode === SpaceviewerMode.D1
        ? get1DPlotLayout(xAxis, widgetWidth ?? 0)
        : getPlotLayout(xAxis, yAxis, (widgetWidth ?? yAxisRangeWidth) - yAxisRangeWidth), [mode, widgetWidth, xAxis, yAxis, yAxisRangeWidth]);
    const xAxisLimits = useMemo(() => getAxisLimits(xAxis), [xAxis]);
    const yAxisLimits = useMemo(() => getAxisLimits(yAxis), [yAxis]);
    const getAxisLabel = useCallback((type, side) => {
        const axis = type === "x" ? xAxis : yAxis;
        const axisLimits = type === "x" ? xAxisLimits : yAxisLimits;
        if (axis?.type === SpaceviewerAxisType.FLOAT) {
            return renderNumberValue(axisLimits[side === "min" ? 0 : 1]);
        }
        const { steps = [] } = { ...axis };
        return steps[axisLimits[side === "min" ? 0 : 1]]?.label;
    }, [xAxis, xAxisLimits, yAxis, yAxisLimits]);
    return (React.createElement("div", { className: styles["spaceviewer-graph"] },
        someResultBiased && React.createElement(BiasedIcon, { left: yAxisRangeWidth }),
        React.createElement("p", { className: styles["yAxis-name"] },
            React.createElement(TextTruncate, { text: yAxis?.name, title: yAxis?.simulationName, className: styles["yAxis-name-label"] })),
        React.createElement(ResizeSensor, { onResize: (entries) => setYAxisRangeWidth(entries[0]?.borderBoxSize[0]?.inlineSize ?? 0) },
            React.createElement("div", { className: styles["yAxis-range"] },
                React.createElement("span", { className: styles["yAxis-range-max"] }, getAxisLabel("y", "max")),
                React.createElement("span", { className: styles["yAxis-range-min"] }, getAxisLabel("y", "min")))),
        React.createElement("div", { className: classNames(styles["plot-wrapper"], { [styles["empty-graph"]]: isEmpty }), style: { height: plotLayout.height } }, !isEmpty && (React.createElement(PlotlyPlot, { className: styles.plot, data: points, layout: plotLayout, config: {
                ...defaultPlotConfig,
                showAxisDragHandles: false,
                showAxisRangeEntryBoxes: false,
                scrollZoom: false,
            }, onClick: handleGraphClick }))),
        React.createElement("p", { className: styles["xAxis-name"] },
            React.createElement(TextTruncate, { text: xAxis?.name, title: xAxis?.simulationName, className: styles["xAxis-name-label"] })),
        React.createElement("div", { className: styles["xAxis-range"], style: {
                marginLeft: yAxisRangeWidth + 3,
                width: `calc(100% - ${yAxisRangeWidth + 3}px)`,
            } },
            React.createElement("span", { className: styles["xAxis-range-min"] }, getAxisLabel("x", "min")),
            React.createElement("span", { className: styles["xAxis-range-max"] }, getAxisLabel("x", "max")))));
};
export default SpaceviewerGraph;
